import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, GeoJSON, Pane, Polyline, CircleMarker, Tooltip as LeafletTooltip, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Coins, Heart, Sparkles, Crown, Compass, ArrowRight, MapPin, Globe,
  TrendingUp, AlertTriangle, Plane,
} from "lucide-react";
import { toast } from "sonner";

import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import PaywallGate from "../components/PaywallGate";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import api from "../lib/api";

// ─── Goal definitions ──────────────────────────────────────────────────────
const GOALS = [
  { key: "money",        label: "Money",        icon: Coins,    grad: "from-amber-400 via-yellow-500 to-orange-600",  blurb: "Wealth corridors, business cities, financial capitals" },
  { key: "love",         label: "Love",         icon: Heart,    grad: "from-rose-400 via-pink-500 to-fuchsia-600",     blurb: "Romance pockets, soulmate zones, marriage triggers" },
  { key: "peace",        label: "Peace",        icon: Compass,  grad: "from-emerald-400 via-teal-500 to-cyan-600",     blurb: "Calm havens, slow-life zones, healing landscapes" },
  { key: "fame",         label: "Fame",         icon: Crown,    grad: "from-yellow-500 via-amber-600 to-red-700",      blurb: "Spotlight cities, creative capitals, public-recognition zones" },
  { key: "spirituality", label: "Spirituality", icon: Sparkles, grad: "from-indigo-500 via-violet-600 to-purple-700",  blurb: "Sacred lands, ashram trails, awakening grounds" },
];

// ─── Planet line colours ───────────────────────────────────────────────────
const PLANET_COLORS = {
  Sun:     "#F59E0B",
  Moon:    "#E5E7EB",
  Mars:    "#EF4444",
  Mercury: "#06B6D4",
  Jupiter: "#FBBF24",
  Venus:   "#EC4899",
  Saturn:  "#3B82F6",
  Rahu:    "#8B5CF6",
  Ketu:    "#9CA3AF",
};
const LINE_TYPE_STYLE = {
  MC: { weight: 2.5, opacity: 0.85, dashArray: null },
  IC: { weight: 1.8, opacity: 0.55, dashArray: "4,4" },
  AC: { weight: 2.2, opacity: 0.80, dashArray: null },
  DC: { weight: 1.8, opacity: 0.55, dashArray: "4,4" },
};

// Helper: split a Polyline that crosses the antimeridian into two safe halves
// so Leaflet doesn't draw a degenerate line across the whole world.
function splitOnAntimeridian(points) {
  if (!points.length) return [];
  const segs = [];
  let cur = [points[0]];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (Math.abs(b[1] - a[1]) > 180) {
      segs.push(cur);
      cur = [b];
    } else {
      cur.push(b);
    }
  }
  if (cur.length) segs.push(cur);
  return segs;
}

function FitToWorld() {
  const map = useMap();
  useEffect(() => {
    map.fitBounds([[-56, -168], [76, 178]], { padding: [6, 6] });
  }, [map]);
  return null;
}

// Self-hosted country outlines (Natural Earth 110m, /public/world-countries.geojson) —
// no third-party tile server or API key, so the map can never be blocked.
let worldGeoCache = null;
const COUNTRY_STYLE = { color: "#8A6A3A", weight: 0.6, opacity: 0.7, fillColor: "#2B2118", fillOpacity: 1 };
function WorldBaseMap() {
  const [geo, setGeo] = useState(worldGeoCache);
  useEffect(() => {
    if (worldGeoCache) return;
    fetch("/world-countries.geojson").then((r) => r.json()).then((d) => { worldGeoCache = d; setGeo(d); }).catch(() => {});
  }, []);
  return (
    <Pane name="astrogps-basemap" style={{ zIndex: 250 }}>
      {geo && <GeoJSON
      data={geo}
      style={() => COUNTRY_STYLE}
      onEachFeature={(f, layer) => f.properties?.n && layer.bindTooltip(f.properties.n, { sticky: true, className: "astrogps-country-tip" })}
      interactive
    />}
    </Pane>
  );
}

export default function AstroGPSPage() {
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  const [placeQuery, setPlaceQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [goal, setGoal] = useState("money");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [visiblePlanets, setVisiblePlanets] = useState(null); // null = all
  const resultRef = useRef(null);
  usePrefillBirthDetails(setForm, setPlaceQuery);

  const submit = async (e) => {
    e?.preventDefault();
    const place = form.place || placeQuery;
    if (!form.date || !form.time || !place) {
      toast.error("Please fill date, time and place");
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const r = await api.post("/astro-gps/analyze", { ...form, place, goal });
      setData(r.data);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to compute Astro GPS");
    } finally {
      setLoading(false);
    }
  };

  // Compose Polyline data for Leaflet from backend lines, filtered by visiblePlanets
  const polylines = useMemo(() => {
    if (!data?.planetary_lines) return [];
    const lines = data.planetary_lines.filter((ln) => !visiblePlanets || visiblePlanets.has(ln.planet));
    const items = [];
    for (const ln of lines) {
      const pts = ln.points.map((p) => [p.lat, p.lon]);
      const segs = splitOnAntimeridian(pts);
      const style = LINE_TYPE_STYLE[ln.type] || LINE_TYPE_STYLE.MC;
      const color = PLANET_COLORS[ln.planet] || "#fff";
      for (const seg of segs) {
        if (seg.length >= 2) items.push({ planet: ln.planet, type: ln.type, color, style, positions: seg });
      }
    }
    return items;
  }, [data, visiblePlanets]);

  const togglePlanet = (planet) => {
    setVisiblePlanets((prev) => {
      const next = new Set(prev ?? Object.keys(PLANET_COLORS));
      if (next.has(planet)) next.delete(planet);
      else next.add(planet);
      return next;
    });
  };

  const isVisible = (planet) => !visiblePlanets || visiblePlanets.has(planet);

  const goalMeta = GOALS.find((g) => g.key === goal);

  return (
    <PageShell>
      <SEO
        title="Astro GPS — Best Cities For Your Destiny | Astrocartography + Vedic"
        description="Find the best cities on Earth for your money, love, peace, fame or spirituality based on your birth chart. Astrocartography lines + Vedic Dig Bala + D9/D10 — for 150 verified cities."
        path="/astro-gps"
        keywords={[
          "astrocartography", "best city for me", "relocation astrology",
          "where should I live based on birth chart", "vedic relocation",
          "money location astrology", "love location astrology", "fame astrology lines",
        ]}
      />

      <div className="bg-gradient-to-br from-[#1A1410] via-[#2A1A0F] to-[#0F0A06] text-amber-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-12 pb-16">
          <div className="label-eyebrow text-amber-300 flex items-center gap-2">
            <Compass size={14} /> Astro GPS · Best Cities For Your Destiny
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight text-amber-50">
            Where on earth does <span className="text-amber-300">your destiny</span> live?
          </h1>
          <p className="text-amber-100/80 text-sm sm:text-base mt-4 font-light max-w-3xl leading-relaxed">
            Every birth chart casts <b className="text-amber-200">planetary lines</b> across the world map — invisible
            corridors where the Sun, Jupiter, Venus, Rahu and the rest exert maximum pull on your career, marriage,
            health and inner life. Astro GPS layers <b className="text-amber-200">classical astrocartography</b> with{" "}
            <b className="text-amber-200">Vedic Dig Bala</b>, <b className="text-amber-200">D10</b> career analysis and{" "}
            <b className="text-amber-200">D9</b> dharma scoring across <b className="text-amber-200">150+ verified cities</b>{" "}
            and ranks them for the goal you actually want next.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-24">
        {/* ── Goal selector ───────────────────────────────────────────────── */}
        <section className="mb-10" data-testid="astrogps-goal-section">
          <div className="label-eyebrow text-muted mb-3">1 · Pick your goal</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {GOALS.map((g) => {
              const Icon = g.icon;
              const active = goal === g.key;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setGoal(g.key)}
                  className={`group relative rounded-2xl overflow-hidden border-2 transition-all duration-200 text-left ${
                    active ? "border-amber-400 shadow-lg shadow-amber-500/20 scale-[1.02]" : "border-transparent hover:border-amber-200"
                  }`}
                  data-testid={`astrogps-goal-${g.key}`}
                >
                  <div className={`p-4 bg-gradient-to-br ${g.grad} text-white`}>
                    <Icon size={26} className="drop-shadow" />
                    <div className="mt-3 font-serif-display text-xl leading-tight">{g.label}</div>
                    <div className="text-white/80 text-[11px] mt-1 leading-snug line-clamp-2">{g.blurb}</div>
                  </div>
                  {active && (
                    <div className="absolute top-2 right-2 bg-amber-400 text-amber-950 text-[9px] font-bold tracking-widest px-2 py-0.5 rounded-full">
                      ACTIVE
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* ── Birth-details form ──────────────────────────────────────────── */}
        <section className="mb-10">
          <div className="label-eyebrow text-muted mb-3">2 · Tell us when & where you were born</div>
          <form onSubmit={submit} className="rounded-2xl p-6 sm:p-7 bg-[#FAF4E8] border border-soft space-y-5 max-w-2xl" data-testid="astrogps-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="space-y-1">
                <span className="text-xs text-muted">Date of birth</span>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                       className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                       data-testid="astrogps-date" required />
              </label>
              <label className="space-y-1">
                <span className="text-xs text-muted">Time of birth</span>
                <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                       className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                       data-testid="astrogps-time" required />
              </label>
            </div>
            <div className="relative">
              <label className="space-y-1 block">
                <span className="text-xs text-muted">Place of birth</span>
                <input
                  type="text" value={placeQuery}
                  onChange={async (e) => {
                    const q = e.target.value;
                    setPlaceQuery(q);
                    if (q.trim().length >= 2) {
                      try {
                        const { data: sug } = await api.get("/places/search", { params: { q: q.trim() } });
                        setSuggestions(sug || []);
                      } catch { /* ignore */ }
                    } else setSuggestions([]);
                  }}
                  placeholder="Start typing — Mumbai, Delhi, London..."
                  className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                  data-testid="astrogps-place"
                />
              </label>
              {suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                  {suggestions.map((s, i) => (
                    <button type="button" key={i}
                      onClick={() => {
                        const label = s.name || s.label;
                        setForm({ ...form, place: label, timezone: s.timezone || "Asia/Kolkata" });
                        setPlaceQuery(label); setSuggestions([]);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                      data-testid={`astrogps-place-option-${i}`}
                    >{s.name || s.label}</button>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" disabled={loading}
                    className="btn-primary rounded-full px-7 py-3 font-medium disabled:opacity-50"
                    data-testid="astrogps-submit">
              {loading ? "Scanning 150 cities & planetary lines…" : (<>Find my best cities <ArrowRight size={16} className="inline ml-1" /></>)}
            </button>
          </form>
        </section>

        {/* ── Results ─────────────────────────────────────────────────────── */}
        {data && (
          <div ref={resultRef} className="space-y-12" data-testid="astrogps-result">
            {/* ── Headline strip ── */}
            <section className="rounded-2xl bg-gradient-to-br from-[#3F2D1B] to-[#1A1410] text-amber-50 p-6 sm:p-8 border border-amber-700/40 shadow-xl">
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="label-eyebrow text-amber-300 flex items-center gap-2">
                  <goalMeta.icon size={14} /> Best for {goalMeta.label}
                </span>
                <span className="text-amber-100/60 text-sm">·</span>
                <span className="text-amber-100/80 text-sm">{data.birth.place}</span>
                <span className="text-amber-100/60 text-sm">·</span>
                <span className="text-amber-100/80 text-sm">{data.birth.lagna} lagna · {data.birth.moon} moon</span>
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 leading-tight">
                Your <span className="text-amber-300">#{1}</span> destination for {goalMeta.label.toLowerCase()} is{" "}
                <span className="text-amber-200">{data.top_cities[0]?.city.name}</span>
                {data.top_cities[0]?.city.country && (
                  <span className="text-amber-100/70">, {data.top_cities[0].city.country}</span>
                )}
              </h2>
              <p className="text-amber-100/80 text-sm sm:text-base mt-3 max-w-3xl leading-relaxed">
                {data.top_cities[0]?.why}
              </p>
            </section>

            {/* ── World map ── */}
            <section data-testid="astrogps-map-section">
              <div className="flex items-baseline justify-between flex-wrap gap-3 mb-3">
                <div>
                  <div className="label-eyebrow text-muted">Your astrocartography map</div>
                  <p className="text-muted text-xs mt-1 max-w-2xl">
                    Each line is one of your 9 planets projected onto Earth at the instant you were born.
                    Solid = MC/AC (career, identity); dashed = IC/DC (home, relationships). Tap a city for details.
                  </p>
                </div>
                <PlanetLegend toggle={togglePlanet} isVisible={isVisible} />
              </div>

              <div className="rounded-2xl overflow-hidden border border-amber-700/40 shadow-2xl h-[300px] sm:h-[520px]">
                <MapContainer
                  center={[20, 20]} zoom={2} minZoom={0} maxZoom={7} zoomSnap={0.25} zoomDelta={0.5} worldCopyJump={false} scrollWheelZoom={true}
                  maxBounds={[[-75, -200], [85, 200]]} attributionControl={false}
                  style={{ height: "100%", width: "100%", background: "#0B1626" }}
                >
                  <WorldBaseMap />
                  <FitToWorld />

                  {polylines.map((p, i) => (
                    <Polyline
                      key={`ln-${i}`}
                      positions={p.positions}
                      pathOptions={{
                        color: p.color,
                        weight: p.style.weight,
                        opacity: p.style.opacity,
                        dashArray: p.style.dashArray,
                      }}
                    >
                      <LeafletTooltip sticky direction="top" opacity={0.9}>
                        <div className="text-[11px] font-semibold">{p.planet} · {p.type}</div>
                      </LeafletTooltip>
                    </Polyline>
                  ))}

                  {/* Birthplace pin */}
                  <CircleMarker
                    center={[data.birth.lat, data.birth.lon]}
                    radius={6}
                    pathOptions={{ color: "#fff", weight: 2, fillColor: "#22D3EE", fillOpacity: 1 }}
                  >
                    <LeafletTooltip permanent={false} direction="top">
                      <span className="text-[11px]">Birth: {data.birth.place}</span>
                    </LeafletTooltip>
                  </CircleMarker>

                  {/* Top cities — golden, top 5 highlighted */}
                  {data.top_cities.slice(0, 12).map((s, idx) => (
                    <CircleMarker
                      key={s.city.slug}
                      center={[s.city.lat, s.city.lon]}
                      radius={idx < 3 ? 9 : idx < 6 ? 7 : 5}
                      pathOptions={{
                        color: idx < 3 ? "#F59E0B" : "#FBBF24",
                        weight: 2,
                        fillColor: idx < 3 ? "#F59E0B" : "#FCD34D",
                        fillOpacity: 0.95,
                      }}
                    >
                      <Popup>
                        <div className="text-[12px]">
                          <div className="font-bold">#{idx + 1} · {s.city.name}</div>
                          <div className="text-gray-600">{s.city.country}</div>
                          <div className="mt-1 text-amber-700 font-semibold">Score {s.score_norm}/100</div>
                        </div>
                      </Popup>
                      <LeafletTooltip direction="top" opacity={0.95}>
                        <span className="text-[11px] font-semibold">#{idx + 1} {s.city.name}</span>
                      </LeafletTooltip>
                    </CircleMarker>
                  ))}

                  {/* Avoid cities — rose */}
                  {data.avoid_cities?.map((s) => (
                    <CircleMarker
                      key={`avoid-${s.city.slug}`}
                      center={[s.city.lat, s.city.lon]}
                      radius={6}
                      pathOptions={{ color: "#F87171", weight: 2, fillColor: "#EF4444", fillOpacity: 0.9 }}
                    >
                      <LeafletTooltip direction="top" opacity={0.95}>
                        <span className="text-[11px] font-semibold">Avoid · {s.city.name}</span>
                      </LeafletTooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>
            </section>

            {/* ── FREE preview: top 3 cities ──────────────────────────────── */}
            <section data-testid="astrogps-preview">
              <div className="label-eyebrow text-muted mb-3 flex items-center gap-2">
                <TrendingUp size={13} /> Your top 3 cities · free preview
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {data.top_cities.slice(0, 3).map((s, idx) => (
                  <CityCard key={s.city.slug} scored={s} rank={idx + 1} />
                ))}
              </div>
            </section>

            {/* ── PREMIUM: full top-12 + zones + avoid ────────────────────── */}
            <PaywallGate
              tool="astro_gps"
              toolLabel="Astro GPS · Best Cities For Your Destiny"
              birthDetails={{
                date: form.date, time: form.time,
                place: form.place || placeQuery, timezone: form.timezone,
              }}
              report={data}
            >
              <div className="space-y-12">
                {/* full top-12 */}
                <section data-testid="astrogps-full-cities">
                  <div className="label-eyebrow text-muted mb-3 flex items-center gap-2">
                    <Globe size={13} /> All 12 best cities · ranked
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {data.top_cities.slice(3, 12).map((s, idx) => (
                      <CityCard key={s.city.slug} scored={s} rank={idx + 4} />
                    ))}
                  </div>
                </section>

                {/* top countries */}
                <section>
                  <div className="label-eyebrow text-muted mb-3">Top countries · by average city-score</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {data.top_countries.map((c, idx) => (
                      <div key={c.country} className="rounded-xl p-4 bg-gradient-to-br from-[#FFF6E0] to-[#FFE7C2] border border-amber-200">
                        <div className="text-amber-700 text-[10px] font-bold tracking-widest">#{idx + 1}</div>
                        <div className="font-serif-display text-lg leading-tight text-[#3F2D1B] mt-1">{c.country}</div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* relocation zones */}
                {data.relocation_zones?.length > 0 && (
                  <section data-testid="astrogps-zones">
                    <div className="label-eyebrow text-muted mb-3 flex items-center gap-2">
                      <Plane size={13} /> Relocation zones · where your lines stack
                    </div>
                    <div className="space-y-3">
                      {data.relocation_zones.map((z) => (
                        <div key={z.label} className="rounded-xl p-5 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
                          <div className="font-serif-display text-xl text-emerald-900">{z.label}</div>
                          <div className="text-emerald-700 text-xs mt-1">{z.countries.join(" · ")} · {z.city_count} cities clustered</div>
                          <p className="text-sm mt-3 text-emerald-950/85 leading-relaxed">{z.headline}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* avoid */}
                {data.avoid_cities?.length > 0 && (
                  <section data-testid="astrogps-avoid">
                    <div className="label-eyebrow text-rose-700 mb-3 flex items-center gap-2">
                      <AlertTriangle size={13} /> Zones to think twice about · for this goal
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {data.avoid_cities.map((s) => (
                        <div key={s.city.slug} className="rounded-xl p-5 bg-gradient-to-br from-rose-50 to-orange-50 border border-rose-200">
                          <div className="font-serif-display text-xl text-rose-900">{s.city.name}, {s.city.country}</div>
                          <p className="text-sm mt-2 text-rose-950/85 leading-relaxed">{s.why}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </PaywallGate>
          </div>
        )}
      </div>
    </PageShell>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────
function CityCard({ scored, rank }) {
  const city = scored.city;
  const [photoFailed, setPhotoFailed] = useState(false);
  const hasPhoto = !photoFailed;
  return (
    <article
      className="group rounded-2xl overflow-hidden bg-white border border-soft hover:border-amber-300 hover:shadow-xl transition-all duration-200"
      data-testid={`astrogps-city-card-${city.slug}`}
    >
      <div className="relative h-44 overflow-hidden">
        {hasPhoto ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/astro-gps/photo/${city.slug}`}
            alt=""
            loading="lazy"
            onError={() => setPhotoFailed(true)}
            data-testid={`astrogps-city-photo-${city.slug}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-amber-300 via-orange-400 to-rose-500 flex items-center justify-center">
            <Globe size={48} className="text-white/60" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute top-3 left-3 bg-amber-400 text-amber-950 text-xs font-bold tracking-widest px-2.5 py-1 rounded-full">
          #{rank}
        </div>
        <div className="absolute top-3 right-3 bg-black/55 text-amber-50 text-xs font-semibold px-2.5 py-1 rounded-full backdrop-blur">
          {scored.score_norm}/100
        </div>
        <div className="absolute bottom-3 left-3 right-3 text-white">
          <div className="font-serif-display text-2xl leading-tight drop-shadow">{city.name}</div>
          <div className="text-white/80 text-xs flex items-center gap-1.5 mt-0.5">
            <MapPin size={11} /> {city.country}
          </div>
        </div>
      </div>
      <div className="p-4">
        <p className="text-sm text-[#3F2D1B] leading-snug">{scored.why}</p>
        {scored.contributors?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {scored.contributors.slice(0, 3).map((c, i) => (
              <span
                key={i}
                className={`text-[10px] tracking-wider font-semibold px-2 py-0.5 rounded-full border ${
                  c.polarity === "boost"
                    ? "bg-amber-50 border-amber-200 text-amber-900"
                    : "bg-rose-50 border-rose-200 text-rose-900"
                }`}
              >
                {c.planet} {c.line}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

function PlanetLegend({ toggle, isVisible }) {
  const planets = Object.keys(PLANET_COLORS);
  return (
    <div className="flex flex-wrap gap-1.5">
      {planets.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => toggle(p)}
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition ${
            isVisible(p) ? "bg-white border-amber-200" : "bg-gray-100 border-gray-200 opacity-40"
          }`}
          data-testid={`astrogps-legend-${p.toLowerCase()}`}
        >
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: PLANET_COLORS[p] }} />
          {p}
        </button>
      ))}
    </div>
  );
}
